import type { ComponentType, ReactNode } from 'react'
import { Navigate } from '@tanstack/react-router'
import type { FeatureFlag } from '@/shared/constants/feature-flags'
import { useFeatureEnabled } from '@/shared/hooks/use-feature-enabled'
import { UnderConstruction } from '../under-construction'

interface FeatureGateOptions {
  redirectTo?: string
}

interface FeatureGateProps extends FeatureGateOptions {
  flag: FeatureFlag
  children: ReactNode
}

export function FeatureGate({ flag, redirectTo, children }: FeatureGateProps) {
  const enabled = useFeatureEnabled(flag)

  if (enabled === undefined) return <></>
  if (!enabled) {
    return redirectTo ? (
      <Navigate to={redirectTo} replace />
    ) : (
      <UnderConstruction />
    )
  }
  return <>{children}</>
}

export function withFeatureGate<P extends object>(
  flag: FeatureFlag,
  Component: ComponentType<P>,
  options: FeatureGateOptions = {},
) {
  function Gated(props: P) {
    return (
      <FeatureGate flag={flag} redirectTo={options.redirectTo}>
        <Component {...props} />
      </FeatureGate>
    )
  }
  Gated.displayName = `withFeatureGate(${Component.displayName ?? Component.name})`
  return Gated
}
